import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEntry,
  getEntryChipHistory,
  getEntryEventHistory,
  getEntrySeasonHistory,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryHistory,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import React, { useEffect } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import { dateLocales } from "../i18n";
import { ChipName, getChipName } from "../utils/chips";
import Entry from "./Entry";
import EntryHistoryTable from "./EntryHistoryTable";
import { getDisplayRoundNumber } from "../utils/events";
import { Main, Secondary, Wrapper } from "./Layout";
import Link from "./Link";
import SubHeading from "./SubHeading";
import Table, { ScrollTable } from "./Table";
import Title from "./Title";
import Movement from "./leagues/Movement";

type Props = WithTranslation;

const EntryHistory = ({ i18n, t }: Props) => {
  const { entryId } = useParams();
  const dispatch = useDispatch<ThunkDispatch>();

  const chipHistory = useSelector((state: RootState) =>
    getEntryChipHistory(state, Number(entryId)),
  );
  const entry = useSelector((state: RootState) =>
    getEntry(state, Number(entryId)),
  );
  const eventHistory = useSelector((state: RootState) =>
    getEntryEventHistory(state, Number(entryId)),
  );
  const seasonHistory = useSelector((state: RootState) =>
    getEntrySeasonHistory(state, Number(entryId)),
  );

  useEffect(() => {
    dispatch(fetchEntryHistory(Number(entryId)));
    dispatch(fetchEntrySummary(Number(entryId)));
  }, [dispatch, entryId]);

  const movementFromIndex = (index: number): React.ReactNode => {
    const thisRank = eventHistory[index].overall_rank;
    const lastRank = index === 0 ? null : eventHistory[index - 1].overall_rank;
    return <Movement lastRank={lastRank} rank={thisRank} />;
  };

  if (!entry) {
    return null;
  }

  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("entryHistory.title", "Entry History")}</Title>
        </Box>

        <Box mb={4} mt={4}>
          <Box mx={2} mb={2}>
            <SubHeading>
              {t("entryHistory.thisSeason", "This Season")}
            </SubHeading>
          </Box>
          <ScrollTable>
            <Table>
              <thead>
                <tr>
                  <th>RD</th>
                  <th>GP</th>
                  <th>PB</th>
                  <th>GR</th>
                  <th>TM</th>
                  <th>TC</th>
                  <th>OP</th>
                  <th>OR</th>
                  <th>kr</th>
                  <th>#</th>
                </tr>
              </thead>
              <tbody>
                {eventHistory.map((eh, i) => (
                  <tr key={eh.event}>
                    <td>
                      <Link to={`/entry/${entry.id}/event/${eh.event}`}>
                        RD{getDisplayRoundNumber(eh.event)}
                      </Link>
                    </td>
                    <td>{eh.points}</td>
                    <td>{eh.points_on_bench}</td>
                    <td>{eh.rank ? eh.rank.toLocaleString() : "-"}</td>
                    <td>{eh.event_transfers}</td>
                    <td>{eh.event_transfers_cost}</td>
                    <td>{eh.total_points}</td>
                    <td>
                      {eh.overall_rank ? eh.overall_rank.toLocaleString() : "-"}
                    </td>
                    <td>{integerToMoney(eh.value, 10)}</td>
                    <td>{movementFromIndex(i)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollTable>
        </Box>

        <Box mb={4}>
          <Box mx={2} mb={2}>
            <SubHeading>{t("entryHistory.chips", "Chips")}</SubHeading>
          </Box>
          <Table>
            <thead>
              <tr>
                <th>{t("entryHistory.date", "Date")}</th>
                <th>{t("entryHistory.name", "Name")}</th>
                <th>{t("entryHistory.active", "Active")}</th>
              </tr>
            </thead>
            <tbody>
              {chipHistory.map((ch) => (
                <tr key={ch.event}>
                  <td>
                    {formatRawAsLocalI18n(ch.time, dateLocales[i18n.language])}
                  </td>
                  <td>{getChipName(ch.name as ChipName, t)}</td>
                  <td>
                    <Link to={`/entry/${entry.id}/event/${ch.event}`}>
                      RD{getDisplayRoundNumber(ch.event)}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>

        <Box mb={4}>
          <Box mx={2} mb={2}>
            <SubHeading>
              {t("entryHistory.previousSeasons", "Previous Seasons")}
            </SubHeading>
          </Box>
          <EntryHistoryTable seasonHistory={seasonHistory} />
        </Box>
      </Main>
      <Secondary>
        <Entry entryId={entry.id} />
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(EntryHistory);
